import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2, Shield, Users, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { adminUserService } from '../../services/adminUserService';
import { AdminUser } from '../../types';
import { customToast } from '../../utils/customToast';
import AddAdminUserModal from '../modals/AddAdminUserModal';
import EditAdminUserModal from '../modals/EditAdminUserModal';
import ConfirmationModal from '../ConfirmationModal';

const AdminUserManagementTab: React.FC = () => {
    const { theme } = useTheme();
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);

    useEffect(() => {
        loadAdminUsers();
    }, []);

    const loadAdminUsers = async (showRefreshIndicator = false) => {
        try {
            if (showRefreshIndicator) setRefreshing(true);
            else setLoading(true);

            const users = await adminUserService.getAllAdminUsers();
            setAdminUsers(users);
        } catch (error: any) {
            console.error('Error loading admin users:', error);
            customToast.error(error.response?.data?.message || 'Failed to load admin users');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await adminUserService.deleteAdminUser(userToDelete.id);
            customToast.success('Admin user deleted successfully');
            loadAdminUsers(true);
            setShowDeleteModal(false);
            setUserToDelete(null);
        } catch (error: any) {
            console.error('Error deleting admin user:', error);
            customToast.error(error.response?.data?.message || 'Failed to delete admin user');
        }
    };

    const countActivePermissions = (user: AdminUser) => {
        return user.permissions.filter(p => p.can_view || p.can_create || p.can_edit || p.can_delete).length;
    };

    const getStatusBadgeColor = (isActive: boolean) => {
        return isActive
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className={`text-${theme === 'dark' ? 'gray-400' : 'gray-600'}`}>
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                    Loading admin users...
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Admin User Management
                    </h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Manage super admin users and their module access permissions
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => loadAdminUsers(true)}
                        disabled={refreshing}
                        className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${theme === 'dark'
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } disabled:opacity-50`}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        <UserPlus className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Add Admin User</span>
                        <span className="sm:hidden">Add User</span>
                    </button>
                </div>
            </div>

            {/* Admin Users Table */}
            <div className={`rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} overflow-hidden shadow-sm`}>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className={theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}>
                            <tr>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                                    User
                                </th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                                    Contact
                                </th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                                    Status
                                </th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                                    Modules Access
                                </th>
                                <th className={`px-6 py-3 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                                    Last Login
                                </th>
                                <th className={`px-6 py-3 text-right text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {adminUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <Users className={`h-12 w-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            No admin users found. Add your first admin user to get started.
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                adminUsers.map((user) => (
                                    <tr key={user.id} className={theme === 'dark' ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white font-semibold">
                                                    {user.full_name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                                                        {user.full_name}
                                                        {user.is_original_admin && (
                                                            <Shield className="h-4 w-4 text-yellow-500" />
                                                        )}
                                                    </div>
                                                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        @{user.username}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                                                {user.email}
                                            </div>
                                            {user.phone && (
                                                <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {user.phone}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(user.is_active)}`}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                                                {countActivePermissions(user)} {countActivePermissions(user) === 1 ? 'module' : 'modules'}
                                            </div>
                                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {user.permissions.map(p => p.module).join(', ').substring(0, 30)}...
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {user.last_login_at
                                                    ? new Date(user.last_login_at).toLocaleDateString()
                                                    : 'Never'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setShowEditModal(true);
                                                    }}
                                                    className={`p-1.5 rounded ${theme === 'dark'
                                                        ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700'
                                                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
                                                        } transition-colors`}
                                                    title="Edit User"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setUserToDelete(user);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    disabled={user.is_original_admin}
                                                    className={`p-1.5 rounded ${user.is_original_admin
                                                        ? 'text-gray-400 cursor-not-allowed'
                                                        : theme === 'dark'
                                                            ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700'
                                                            : 'text-gray-600 hover:text-red-600 hover:bg-gray-100'
                                                        } transition-colors`}
                                                    title={user.is_original_admin ? 'Cannot delete original super admin' : 'Delete User'}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modals */}
            {showAddModal && (
                <AddAdminUserModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        loadAdminUsers(true);
                    }}
                />
            )}

            {showEditModal && selectedUser && (
                <EditAdminUserModal
                    user={selectedUser}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedUser(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedUser(null);
                        loadAdminUsers(true);
                    }}
                />
            )}

            {showDeleteModal && userToDelete && (
                <ConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setUserToDelete(null);
                    }}
                    onConfirm={handleDeleteUser}
                    title="Delete Admin User"
                    message={`Are you sure you want to delete "${userToDelete.full_name}"? This action cannot be undone.`}
                    confirmText="Delete"
                    confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
                />
            )}
        </div>
    );
};

export default AdminUserManagementTab;
